import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, MessageSquare, Mail, Send, Smartphone, Moon, Bell } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  notificationsApi,
  type NotificationPreferences,
  type CategoryOverride,
} from '@/api/notifications'
import { usePushNotifications } from '@/hooks/usePushNotifications'

const CHANNEL_CONFIG = [
  { key: 'whatsapp' as const, label: 'WhatsApp', description: 'Receive notifications via WhatsApp', icon: MessageSquare },
  { key: 'email' as const, label: 'Email', description: 'Receive notifications via email', icon: Mail },
  { key: 'telegram' as const, label: 'Telegram', description: 'Receive notifications via Telegram', icon: Send },
  { key: 'push' as const, label: 'Push', description: 'Browser push notifications', icon: Smartphone },
]

type ChannelKey = 'whatsapp' | 'email' | 'telegram' | 'push'

const CATEGORY_CONFIG = [
  { key: 'deadline_reminder', label: 'Deadline Reminders', description: 'Upcoming statutory deadlines' },
  { key: 'overdue_escalation', label: 'Overdue Escalations', description: 'Escalation for missed deadlines' },
  { key: 'morning_summary', label: 'Morning Summary', description: 'Daily task and deadline digest' },
  { key: 'task_assignment', label: 'Task Assignment', description: 'When you are assigned a task' },
  { key: 'task_update', label: 'Task Updates', description: 'Status changes on your tasks' },
  { key: 'client_reminder', label: 'Client Reminders', description: 'Client follow-up reminders' },
]

const OVERRIDE_CHANNELS: { key: keyof Omit<CategoryOverride, 'bypass_quiet_hours'>; label: string }[] = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email' },
  { key: 'telegram', label: 'Telegram' },
]

function SettingsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-9 rounded-full" />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-5 w-9 rounded-full" />
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-9 w-32" />
        </CardContent>
      </Card>
    </div>
  )
}

export function NotificationSettings() {
  const queryClient = useQueryClient()
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const push = usePushNotifications()

  const { data: prefs, isLoading, isError } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: notificationsApi.getPreferences,
    retry: 1,
  })

  const mutation = useMutation({
    mutationFn: (update: Partial<NotificationPreferences>) =>
      notificationsApi.updatePreferences(update),
    onSuccess: (data) => {
      queryClient.setQueryData(['notification-preferences'], data)
      setSaveStatus('Saved')
      setTimeout(() => setSaveStatus(null), 2000)
    },
    onError: () => {
      setSaveStatus('Failed to save')
      setTimeout(() => setSaveStatus(null), 3000)
    },
  })

  const handleChannelToggle = async (channel: ChannelKey, value: boolean) => {
    if (channel === 'push') {
      if (value) {
        const result = await push.subscribe()
        if (!result.success) {
          setSaveStatus(result.message)
          setTimeout(() => setSaveStatus(null), 4000)
          return
        }
      } else {
        await push.unsubscribe()
      }
    }
    mutation.mutate({ [channel]: value })
  }

  const handleQuietHoursToggle = (enabled: boolean) => {
    const current = prefs?.quiet_hours ?? { enabled: false, start: '22:00', end: '07:00', timezone: 'Asia/Kolkata' }
    mutation.mutate({
      quiet_hours: { ...current, enabled },
    })
  }

  const handleQuietHoursTime = (field: 'start' | 'end', value: string) => {
    const current = prefs?.quiet_hours ?? { enabled: true, start: '22:00', end: '07:00', timezone: 'Asia/Kolkata' }
    mutation.mutate({
      quiet_hours: { ...current, [field]: value },
    })
  }

  const handleCategoryOverride = (
    category: string,
    field: keyof CategoryOverride,
    value: boolean
  ) => {
    const existingOverrides = prefs?.category_overrides ?? {}
    const existing = existingOverrides[category] ?? {
      whatsapp: null,
      email: null,
      telegram: null,
      bypass_quiet_hours: false,
    }
    mutation.mutate({
      category_overrides: {
        ...existingOverrides,
        [category]: { ...existing, [field]: value },
      },
    })
  }

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <SettingsSkeleton />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Notification Settings</h1>
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground text-center">
              Unable to load notification settings. The feature may not be available yet.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const quietHours = prefs?.quiet_hours ?? { enabled: false, start: '22:00', end: '07:00', timezone: 'Asia/Kolkata' }
  const categoryOverrides = prefs?.category_overrides ?? {}

  // Push toggle reflects actual subscription state, not just the backend preference
  const getPushChecked = (): boolean => {
    if (push.subscribed) return true
    // Fall back to backend pref if hook is still loading
    if (push.loading) return prefs?.push ?? false
    return false
  }

  const getPushDescription = (): string => {
    if (push.loading) return 'Browser push notifications'
    if (!push.supported) return 'Not supported in this browser'
    if (push.subscribed) return 'Push notifications active'
    if (push.permissionGranted) return 'Permission granted — enable to subscribe'
    return 'Browser push notifications'
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notification Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure how and when you receive notifications
          </p>
        </div>
        {saveStatus && (
          <span className={cn(
            'text-xs font-medium px-2.5 py-1 rounded-full transition-opacity',
            saveStatus === 'Saved'
              ? 'bg-primary/10 text-primary'
              : 'bg-destructive/10 text-destructive'
          )}>
            {saveStatus}
          </span>
        )}
      </div>

      {/* Section 1: Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notification Channels</CardTitle>
          <CardDescription>Choose which channels can deliver notifications to you</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {CHANNEL_CONFIG.map(({ key, label, description, icon: Icon }) => {
            const isPush = key === 'push'
            const checked = isPush ? getPushChecked() : (prefs?.[key] ?? false)
            const desc = isPush ? getPushDescription() : description
            const disabled = mutation.isPending || (isPush && push.loading) || (isPush && !push.supported)

            return (
              <div key={key} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <Label className="text-sm font-medium">{label}</Label>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </div>
                <Switch
                  checked={checked}
                  onCheckedChange={(val) => handleChannelToggle(key, val)}
                  disabled={disabled}
                />
              </div>
            )
          })}
        </CardContent>
      </Card>

      {/* Section 2: Quiet Hours */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">Quiet Hours</CardTitle>
              <CardDescription>Suppress non-critical notifications during these hours</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Enable quiet hours</Label>
            <Switch
              checked={quietHours.enabled}
              onCheckedChange={handleQuietHoursToggle}
              disabled={mutation.isPending}
            />
          </div>
          {quietHours.enabled && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Start time</Label>
                <Input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => handleQuietHoursTime('start', e.target.value)}
                  disabled={mutation.isPending}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">End time</Label>
                <Input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => handleQuietHoursTime('end', e.target.value)}
                  disabled={mutation.isPending}
                />
              </div>
              <p className="col-span-2 text-[11px] text-muted-foreground">
                Times are in IST (Asia/Kolkata)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Per-Category Overrides */}
      <Collapsible>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/20 transition-colors rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <CardTitle className="text-base">Per-Category Overrides</CardTitle>
                    <CardDescription>Fine-tune notifications for each category</CardDescription>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform data-[state=open]:rotate-180" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6 pt-2">
              {CATEGORY_CONFIG.map(({ key, label, description }) => {
                const override = categoryOverrides[key] ?? {
                  whatsapp: null,
                  email: null,
                  telegram: null,
                  bypass_quiet_hours: false,
                }

                return (
                  <div key={key} className="space-y-3 pb-4 border-b border-border/30 last:border-b-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pl-1">
                      {OVERRIDE_CHANNELS.map((ch) => {
                        const isInherited = override[ch.key] === null || override[ch.key] === undefined
                        return (
                          <div key={ch.key} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              <Label className="text-xs">{ch.label}</Label>
                              {isInherited && (
                                <span className="text-[10px] text-muted-foreground/60 italic">
                                  (global)
                                </span>
                              )}
                            </div>
                            <Switch
                              checked={override[ch.key] ?? false}
                              onCheckedChange={(checked) => handleCategoryOverride(key, ch.key, checked)}
                              disabled={mutation.isPending}
                            />
                          </div>
                        )
                      })}
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">Bypass quiet hours</Label>
                        <Switch
                          checked={override.bypass_quiet_hours}
                          onCheckedChange={(checked) => handleCategoryOverride(key, 'bypass_quiet_hours', checked)}
                          disabled={mutation.isPending}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  )
}
